import { createActions, createReducer } from "reduxsauce"
import { v4 as uuid } from "uuid"
import moment from "moment"
import uploadFiles from "api/file-upload"
import { filter, get, first } from "lodash"
import { takeLatest, select, put, call, getContext } from "redux-saga/effects"
import { post, get as apiGet } from "api"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as serverNotificationsCreators } from "modules/server-notifications/model"
import { Creators as dialogCreators } from "modules/dialog/model"
import { Creators as researchTopicCreators } from "modules/research-topic/model"
import { Creators as modelCreators } from "model/data"
import { getExperiment, getModelId } from "./selectors"
import { log } from "helpers/logger"

export const { Types, Creators } = createActions({
  submitStepSuccess: ["step"],
  addFiles: ["files"],
  removeFile: ["file"],
  scanFiles: ["files", "topicId"],
  scanFilesSuccess: ["result", "uploadedFiles"],
  scanFilesError: [],
  confirmUpload: ["uploadOption", "topicId"],
  confirmUploadError: [],
  resetUpload: [],
  resetScan: [],
  changeUploadOption: ["uploadOption"],
  setModel: ["modelId"],
  setExperimentDates: ["dates"],
}, {
  prefix: "ompnt/modules/experimentPositions/",
})

const INITIAL_STATE = {
  activeStep: 0,
  scanResult: null,
  files: [],
  uploadedFiles: [],
  uploadOption: "",
  uploadDateTime: null,
  modelId: "",
  experimentDates: [],
}

function handleSubmitStepSuccess(state, step) {
  return { ...state, activeStep: step }
}

function handleAddFiles(state, { files }) {
  const newFiles = files.map(file => ({ id: uuid(), file }))
  return { ...state, files: [...state.files, ...newFiles] }
}

function handleRemoveFile(state, { file: { id } }) {
  return { ...state, files: filter(state.files, file => file.id !== id) }
}

function handleScanFiles(state) {
  return { ...state }
}

function handleScanFilesSuccess(state, { result, uploadedFiles }) {
  return { ...state, activeStep: 1, scanResult: result, uploadDateTime: moment(), uploadedFiles }
}

function handleScanFilesError(state) {
  return { ...state, scanResult: null }
}

function handleConfirmUpload(state) {
  return { ...state }
}

function handleConfirmUploadError(state) {
  return { ...state }
}

function handleResetScan(state) {
  return { ...state, scanResult: null, activeStep: 0, uploadOption: "", uploadDateTime: null }
}

function handleResetUpload(state) {
  return { ...state, scanResult: null, files: [], activeStep: 0, uploadOption: "", uploadDateTime: null }
}

function handleChangeUploadOption(state, { uploadOption }) {
  return { ...state, uploadOption }
}

function handleSetModel(state, { modelId }) {
  return { ...state, modelId }
}

function handleSetExperimentDates(state, { dates }) {
  return { ...state, experimentDates: dates }
}

export const HANDLERS = {
  [Types.SUBMIT_STEP_SUCCESS]: handleSubmitStepSuccess,
  [Types.ADD_FILES]: handleAddFiles,
  [Types.REMOVE_FILE]: handleRemoveFile,
  [Types.SCAN_FILES]: handleScanFiles,
  [Types.SCAN_FILES_SUCCESS]: handleScanFilesSuccess,
  [Types.SCAN_FILES_ERROR]: handleScanFilesError,
  [Types.CONFIRM_UPLOAD]: handleConfirmUpload,
  [Types.CONFIRM_UPLOAD_ERROR]: handleConfirmUploadError,
  [Types.RESET_UPLOAD]: handleResetUpload,
  [Types.RESET_SCAN]: handleResetScan,
  [Types.CHANGE_UPLOAD_OPTION]: handleChangeUploadOption,
  [Types.SET_MODEL]: handleSetModel,
  [Types.SET_EXPERIMENT_DATES]: handleSetExperimentDates,
}

export default createReducer(INITIAL_STATE, HANDLERS)

function* scanFiles({ files, topicId }) {
  try {
    yield put(Creators.setExperimentDates([]))
    if (files.length > 0) {
      const { account, modelId, experiment } = yield select(state => ({
        account: state.global.selected.account,
        modelId: getModelId(state),
        experiment: getExperiment(state),
      }))

      yield put(dialogCreators.open({ title: "Your Files Are Being Scanned", contentText: "Please wait while we scan your data", disableEscapeKeyDown: true, loadingDialog: true, disableBackdropClick: true }))

      const resourceId = experiment.id
      const uploadedFiles = yield call(uploadFiles, account, resourceId, files, { type: "experiment" })

      if (uploadedFiles) {
        const doctorParams = { filepaths: uploadedFiles, modelId }
        const doctorURL = `/api/${account}/experiments/${experiment.id}/portfolio-doctor`

        const { portfolioDoctorResult } = yield call(post, doctorURL, doctorParams)

        if (!portfolioDoctorResult) {
          yield put(Creators.scanFilesError())
          yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
        } else if (portfolioDoctorResult.errors.length > 0) {
          yield put(Creators.scanFilesError())
          yield put(notificationCreators.add("error", null, get(first(portfolioDoctorResult.errors), "message")))
        } else if (portfolioDoctorResult.data) {
          yield put(Creators.scanFilesSuccess(portfolioDoctorResult.data, uploadedFiles))
          const { navigate } = yield getContext("router")
          yield call(navigate, `/${account}/t/${topicId}/experiments/${experiment.id}/upload/preview`)
        }

        if (portfolioDoctorResult.messages.length > 0) {
          yield put(notificationCreators.add("warning", null, get(first(portfolioDoctorResult.messages), "message")))
        }
      } else {
        yield put(Creators.scanFilesError())
        yield put(notificationCreators.add("error", null, "Something bad happened while uploading the files."))
      }

      yield put(dialogCreators.close())
    }
  } catch (err) {
    let logErr = err
    if (!(err instanceof Error)) {
      logErr = new Error(JSON.stringify(err))
    }

    yield call(log, logErr)
    yield put(Creators.scanFilesError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
    yield put(dialogCreators.close())
  }
}

function* confirmUpload({ uploadOption, topicId }) {
  try {
    const { uploadedFiles, modelId, account, experiment } = yield select(state => ({
      uploadedFiles: state.modules.experimentPositions.uploadedFiles,
      account: state.global.selected.account,
      modelId: getModelId(state),
      experiment: getExperiment(state),
    }))

    yield put(dialogCreators.open({
      title: uploadedFiles.length > 1 ? "Your Files Are Being Uploaded" : "Your File Is Being Uploaded",
      contentText: "Please wait while we upload your data",
      disableEscapeKeyDown: true,
      loadingDialog: true,
      disableBackdropClick: true,
    }))

    const deleteDates = uploadOption === "clear"
    const doctorParams = { filepaths: uploadedFiles, upload: true, deleteDates }
    const doctorURL = `/api/${account}/experiments/${experiment.id}/portfolio-doctor`

    doctorParams.modelId = modelId

    const { portfolioDoctorResult } = yield call(post, doctorURL, doctorParams)

    if (portfolioDoctorResult !== "OK") {
      yield put(Creators.confirmUploadError())
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while scanning the files."))
      yield put(dialogCreators.close())
    } else {
      yield put(dialogCreators.close())
      yield put(serverNotificationsCreators.open())
      yield put(Creators.resetUpload())

      yield put(modelCreators.resetCollections(["topicExperiments", "experiments"]))
      yield put(researchTopicCreators.loadNewExperiment({ id: experiment.id, label: experiment.label, type: "experiment", parentType: "research_topic", uploading: true }))

      const { navigate } = yield getContext("router")
      yield call(navigate, `/${account}/t/${topicId}/experiments/summary`)
    }
  } catch {
    yield put(Creators.confirmUploadError())
    yield put(notificationCreators.add("error", null, "An unexpected error occurred while uploading the positions."))
    yield put(dialogCreators.close())
  }
}

function* getExperimentDates() {
  const { account, experiment, modelId } = yield select(state => ({
    account: state.global.selected.account,
    modelId: getModelId(state),
    experiment: getExperiment(state),
  }))

  const apiUrl = `/api/${account}/e/${experiment.id}/${modelId}`
  const dates = yield call(apiGet, apiUrl)
  yield put(Creators.setExperimentDates(dates))
}

export function* saga() {
  yield takeLatest(Types.SCAN_FILES, scanFiles)
  yield takeLatest(Types.CONFIRM_UPLOAD, confirmUpload)
  yield takeLatest(Types.SCAN_FILES_SUCCESS, getExperimentDates)
}
